import {
    MeshStandardMaterial,
} from "three"

/**
 * Material Pool - Gère un pool de matériaux réutilisables
 * pour éviter de créer des centaines de matériaux identiques
 */
class MaterialPool {
    constructor() {
        this.pools = new Map();
        this.maxPoolSize = 50; // Nombre max de matériaux par pool
    }

    /**
     * Récupère un matériau du pool ou en crée un nouveau
     * @param {string} type - Type de matériau ('tooth', 'beigeTooth', 'verticeTooth')
     * @returns {MeshStandardMaterial}
     */
    acquire(type) {
        if (!this.pools.has(type)) {
            this.pools.set(type, []);
        }

        const pool = this.pools.get(type);
        
        // Retourner un matériau existant s'il y en a un disponible
        if (pool.length > 0) {
            const material = pool.pop();
            material.visible = true;
            material.isFromPool = true;
            return material;
        }

        // Sinon en créer un nouveau
        const material = this._createMaterial(type);
        material.isFromPool = true;
        return material;
    }

    /**
     * Rend un matériau au pool pour réutilisation
     * @param {string} type - Type de matériau
     * @param {MeshStandardMaterial} material - Matériau à rendre
     */
    release(type, material) {
        if (!this.pools.has(type)) {
            this.pools.set(type, []);
        }

        const pool = this.pools.get(type);
        
        // Ne pas garder plus de matériaux que la taille max
        if (pool.length < this.maxPoolSize) {
            // Réinitialiser le matériau
            material.opacity = 1;
            material.visible = false;
            pool.push(material);
        } else {
            // Disposer si le pool est plein
            material.dispose();
        }
    }

    /**
     * Crée un nouveau matériau selon le type
     * @private
     */
    _createMaterial(type) {
        switch (type) {
            case 'tooth':
                return new MeshStandardMaterial({
                    color: 0xfbf9f5,
                    metalness: 0.2,
                    roughness: 0.4,
                    opacity: 1,
                    transparent: false,
                });

            case 'beigeTooth':
                return new MeshStandardMaterial({
                    color: 0xfaebd2,
                    metalness: 0.2,
                    roughness: 0.4,
                    transparent: false,
                    opacity: 1,
                });

            case 'verticeTooth':
                return new MeshStandardMaterial({
                    color: 0xfbf9f5,
                    metalness: 0.2,
                    roughness: 0.4,
                    transparent: false,
                    vertexColors: true,
                    opacity: 1,
                });

            default:
                throw new Error(`Unknown material type: ${type}`);
        }
    }

    /**
     * Nettoie tous les pools et dispose tous les matériaux
     */
    dispose() {
        this.pools.forEach((pool) => {
            pool.forEach(material => {
                if (material.map) material.map.dispose();
                material.dispose();
            });
            pool.length = 0;
        });
        this.pools.clear();
    }

    /**
     * Affiche les statistiques du pool
     */
    getStats() {
        const stats = {};
        this.pools.forEach((pool, type) => {
            stats[type] = pool.length;
        });
        return stats;
    }
}

// Instance singleton
const materialPool = new MaterialPool();

export default materialPool;
export { MaterialPool };
